import { Descriptions } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import {
  archiveMessage,
  deleteReceivedMessages,
  getReceivedMessage,
} from '../../../api/messages.api';
import ErrorResponse from '../../../components/ErrorResponse';
import Loader from '../../../components/Loader';
import useAxios from '../../../hooks/useAxios';
import { IGetMessage } from '../../../models/messages.model';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import moment from 'moment';
import Button from '../../../components/Button';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import useNotification from '../../../hooks/useNotification';
import useAuth from '../../../hooks/useAuth';

interface IMatch {
  messageCode: string;
}

const MessageReceiveRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const messageCode = Number(match.params.messageCode);
  const history = useHistory();
  const location = useLocation();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetMessage>();
  const {
    data: archiveRes,
    isLoading: archiveIsLoading,
    error: archiveError,
    execute: archiveExecute,
  } = useAxios();
  const {
    data: deleteRes,
    isLoading: deleteIsLoading,
    error: deleteError,
    execute: deleteExecute,
  } = useAxios();

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    if (messageCode) {
      execute(getReceivedMessage(messageCode));
    }
  }, [messageCode]);

  useEffect(() => {
    if (archiveRes && !archiveIsLoading) {
      successNotification(t('message.receive.message1'));
      history.push('/message/archive');
    }
  }, [archiveRes, archiveIsLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      successNotification(t('common.message.delete'));
      history.push(`/message/receive${location.search}`);
    }
  }, [deleteRes, deleteIsLoading]);

  const onArchive = () => {
    archiveExecute(archiveMessage(messageCode));
  };

  const onDelete = () => {
    deleteExecute(deleteReceivedMessages([messageCode]));
  };

  return (
    <div className="content scrollbar">
      {isLoading || archiveIsLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={archiveError} />
      <ErrorResponse error={deleteError} />
      <SimpleHeader parentMenu={t('common.menu.message')} currentMenu={t('message.receive.title1')} />

      <div className='comm-table__area rowType'>
        <div>
          <Descriptions
            bordered
            column={1}
            size="middle"
            labelStyle={{ width: '200px' }}
          >
            <Descriptions.Item label={t('common.column.title')}>
              {res?.data.title}
            </Descriptions.Item>
            <Descriptions.Item label={t('message.archive.column1')}>
              {res?.data.sendMemKorName} ({res?.data.sendWebId})
            </Descriptions.Item>
            <Descriptions.Item label={t('message.archive.column3')}>
              {res?.data.recipientInfo.map((item) => (
                <span className="mr-3" key={item.recipientMemCode}>
                  {item.recipientMemKorName} ({item.recipientWebId})
                </span>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label={t('message.archive.column4')}>
              {moment(res?.data.sendDate).format('YYYY.MM.DD HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.column.contents')}>
              <div
                dangerouslySetInnerHTML={{ __html: res?.data.content || '' }}
              />
            </Descriptions.Item>
            <Descriptions.Item label={t('common.column.file')}>
              {res?.data.files ? (
                res.data.files.map((item: any) => (
                  <div>
                    <a
                      className="download-link"
                      href={`${item.fileLocation}`}
                      download={item.originalName}
                    >
                      {item.originalName}
                    </a>
                  </div>
                ))
              ) : (
                <div />
              )}
            </Descriptions.Item>
          </Descriptions>
        </div>
        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className="btn-basic gray-line"
              onClick={() => history.push(`/message/receive${location.search}`)}
              text={t('common.button.list')}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic pink-line"
              onClick={onArchive}
              text={t('common.button.keep')}
            />
            <Button
              className="btn-basic pink-line"
              onClick={() => {
                sessionStorage.setItem('title', `Re: ${res?.data.title}`);
                sessionStorage.setItem(
                  'memberCode',
                  res?.data.sendMemCode || '',
                );
                history.push('/message/write');
              }}
              text={t('common.button.reply')}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn-basic pink-line"
              onClick={() => {
                sessionStorage.setItem('title', `Fw: ${res?.data.title}`);
                sessionStorage.setItem('content', res?.data.content || '');
                sessionStorage.setItem(
                  'messageCode',
                  String(res?.data.messageCode || ''),
                );
                sessionStorage.setItem(
                  'attachFileCode',
                  String(res?.data.attachFileCode || ''),
                );
                sessionStorage.setItem(
                  'files',
                  JSON.stringify(res?.data.files),
                );
                history.push('/message/write');
              }}
              text={t('common.button.relay')}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn-basic gray"
              onClick={() => setConfirmDeleteVisible(true)}
              text={t('common.button.delete')}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('message.archive.title2')}
          messageText={t('message.archive.text1')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </div>
  );
};

export default MessageReceiveRead;
